import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import * as Sentry from '@sentry/browser'
import {
  BulkAssignerEmployeesDocument,
  BulkUpdateDocument,
  BulkUpdateDocumentsDocumentDocumentsInput,
  BulkUpdateMutation,
} from 'generated/resources/graphql'

import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import { UploadProcess } from 'modules/documents/hooks/useGetUploadProcess'
import {
  UploadProcessDocument,
  useGetUploadProcessDocuments,
} from 'modules/documents/hooks/useGetUploadProcessDocuments'
import { AssignEsignatureForm } from 'modules/documents/lib/forms/AssignEsignatureForm'

import BulkModal from './BulkModal'
import { uploadProcessDocumentsToMatches } from './filterDocuments'

interface Props {
  uploadProcess: UploadProcess
  documents: Document[]
  onFinished?: () => void
}

const BulkAssigner = ({ uploadProcess, documents, onFinished }: Props) => {
  const [isFetchingScores, setIsFetchingScores] = React.useState<boolean>(false)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false)
  const [selectedIdsForEsignature, setSelectedIdsForEsignature] = React.useState<string[]>([])
  const [uploadProcessDocuments, setUploadProcessDocuments] = React.useState<
    UploadProcessDocument[]
  >([])
  const [bulkUpdate] = useMutation(BulkUpdateDocument, {
    extractorFn: (data: BulkUpdateMutation) => data.documentsMutations.bulkUpdateDocumentsDocument,
  })
  const [fetchEmployees] = useLazyQuery(BulkAssignerEmployeesDocument)
  const requestEsignatureForm = React.useRef(
    new AssignEsignatureForm({
      requestEsignature: false,
    })
  ).current

  const { fetch, loading: loadingUploadProcessDocuments } = useGetUploadProcessDocuments()

  useEffect(() => {
    setIsFetchingScores(loadingUploadProcessDocuments)
  }, [loadingUploadProcessDocuments])

  useUnsafeEffect(() => {
    setSelectedIdsForEsignature(uploadProcess.additionalSigners.map((signer) => signer.id))
  }, [uploadProcess.additionalSigners])

  const onClickOpenModal = () => {
    setIsModalOpen(true)

    fetchUploadProcessDocuments()
  }

  const onClose = () => {
    setIsModalOpen(false)
    setIsFetchingScores(true)
  }

  const onSelectIdsForEsignature = (selectedIdsForEsignature: string[]) => {
    setSelectedIdsForEsignature(selectedIdsForEsignature)
  }

  const uploadProcessDocumentChunks = (documentIds: string[]): string[][] => {
    const chunks = []
    const chunkSize = 100

    for (let i = 0; i < documentIds.length; i += chunkSize) {
      chunks.push(documentIds.slice(i, i + chunkSize))
    }

    return chunks
  }

  const fetchUploadProcessDocuments = async () => {
    setIsFetchingScores(true)

    const documentsChunk = uploadProcessDocumentChunks(documents.map((doc) => doc.id))

    const result = await Promise.all(
      documentsChunk.map((documentIds) =>
        fetch({
          documentIds: documentIds,
        })
      )
    )

    setUploadProcessDocuments(result.flat())

    setIsFetchingScores(false)
  }

  const processScores = () => {
    const ids = uploadProcessDocuments.map((doc) => doc.documentId)

    const matches = uploadProcessDocumentsToMatches(ids, uploadProcessDocuments)

    // NOTE: we're only interested in perfect matches,
    // we discard documents with more than one match
    const documentSingleMatches = Object.keys(matches).filter((key) => matches[key]?.length === 1)

    const countMatches = documentSingleMatches.length
    const remainingDocs = documents.length - countMatches

    return { countMatches, remainingDocs, documentSingleMatches, matches }
  }

  const {
    countMatches,
    remainingDocs,
    documentSingleMatches,
    matches: documentMatches,
  } = processScores()

  const fetchAllEmployees = async (ids: string[]): Promise<Map<string, string>> => {
    const employeeAccessMap = new Map<string, string>()
    const PAGE_SIZE = 1000
    let after: string | undefined
    let hasNextPage = true

    while (hasNextPage) {
      const { data } = await fetchEmployees({
        variables: { ids, first: PAGE_SIZE, after },
      })

      const connection = data?.employees?.employeesConnection
      for (const employee of connection?.nodes ?? []) {
        employeeAccessMap.set(employee.id, employee.accessId)
      }

      hasNextPage = connection?.pageInfo?.hasNextPage ?? false
      after = connection?.pageInfo?.endCursor ?? undefined
    }

    return employeeAccessMap
  }

  const assignDocuments = async () => {
    setIsLoading(true)

    try {
      // Collect unique employee IDs from matches to fetch their accessIds via GraphQL
      const uniqueEmployeeIds = Array.from(
        new Set(
          documentSingleMatches
            .map((docId) => documentMatches[docId]?.[0])
            .filter((id): id is string => id != null)
        )
      )

      const employeeAccessMap = await fetchAllEmployees(uniqueEmployeeIds)

      const { docs, skippedEmployeeIds } = docsToUpdate(employeeAccessMap)

      if (skippedEmployeeIds.length > 0) {
        // This shouldn't happen: users should only be able to match documents
        // against employees they can see. Reaching this branch means the
        // backend silently filtered ids we requested (e.g. through an
        // authorization boundary), which is a visibility mismatch we want to
        // investigate.
        Sentry.captureException(
          new Error('BulkAssigner: matched employees missing from authorized response'),
          {
            extra: {
              uploadProcessId: uploadProcess.id,
              requestedCount: uniqueEmployeeIds.length,
              skippedCount: skippedEmployeeIds.length,
              skippedEmployeeIds,
            },
          }
        )
      }

      if (docs.length === 0) {
        return
      }

      const response = await bulkUpdate({
        variables: { documents: docs },
      })

      const result = handleResult(response, () => undefined)

      if (result.success) {
        onFinished && onFinished()
        setIsModalOpen(false)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const docsToUpdate = (
    employeeAccessMap: Map<string, string>
  ): {
    docs: BulkUpdateDocumentsDocumentDocumentsInput[]
    skippedEmployeeIds: string[]
  } => {
    const requestEsignature = uploadProcess.esignatureRequest ?? false
    const skippedEmployeeIds: string[] = []

    const docs = documentSingleMatches
      .map((docId) => {
        const employeeId = documentMatches[docId]?.[0]
        const accessId = employeeId ? employeeAccessMap.get(employeeId) : undefined

        if (!employeeId || !accessId) {
          if (employeeId) skippedEmployeeIds.push(employeeId)
          return null
        }

        const signees = [accessId, ...selectedIdsForEsignature]
        const uniqueSignees = Array.from(new Set(signees))

        const options: BulkUpdateDocumentsDocumentDocumentsInput = {
          documentId: docId,
          employeeId: employeeId,
          signees: uniqueSignees,
          requestEsignature: requestEsignature,
          public: true,
          isPendingAssignment: false,
          distributionMethod: 'bulk_match',
        }

        return options
      })
      .filter((doc): doc is BulkUpdateDocumentsDocumentDocumentsInput => doc !== null)

    return { docs, skippedEmployeeIds }
  }

  return (
    <>
      <Button type="secondary" onClick={onClickOpenModal} iconName={'check-circle-line'}>
        {i18n.t('split_documents.header.auto_send_v2')}
      </Button>
      {isModalOpen && (
        <BulkModal
          selectedIdsForEsignature={selectedIdsForEsignature}
          requestEsignatureForm={requestEsignatureForm}
          onSelectIdsForEsignature={onSelectIdsForEsignature}
          onClose={onClose}
          onClick={assignDocuments}
          isLoading={isLoading}
          isFetchingScores={isFetchingScores}
          countMatches={countMatches}
          remainingDocs={remainingDocs}
          uploadProcess={uploadProcess}
        />
      )}
    </>
  )
}

export default BulkAssigner
